/**
 * A collectable food item that spawns in the maze, persists for a limited
 * time, then fades out and self-destructs.
 *
 * Food types: fruit, chicken leg, tuna can - each uses a different
 * procedurally generated texture.
 *
 * When collected, the food name pops up at the collection spot with a
 * zoom-in effect then fades away, giving visual feedback.
 */

import Phaser from 'phaser';
import { FOOD_TEXTURE_MAP, FOOD_NAME_MAP, FOOD_AUDIO_MAP, FOOD_HEAL_MAP, FoodType } from '../utils/constants.js';
import {
  FOOD_FADE_DURATION,
  FOOD_HEAL_AMOUNT,
  TILE_SIZE,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

export default class FoodItem extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x          - World x position (center of tile).
   * @param {number} y          - World y position (center of tile).
   * @param {string} foodType   - One of FoodType enum values.
   * @param {number} lifetime   - Milliseconds before the item fades and disappears.
   * @param {number[][]} grid   - The maze grid (unused now, kept for API compatibility).
   */
  constructor(scene, x, y, foodType, lifetime, grid) {
    const textureKey = FOOD_TEXTURE_MAP[foodType];
    super(scene, x, y, textureKey);

    this.scene = scene;
    this.foodType = foodType;
    this.healAmount = FOOD_HEAL_MAP[foodType] ?? FOOD_HEAL_AMOUNT;

    /** @type {number[][]} */
    this._grid = grid;

    // Add to scene
    scene.add.existing(this);

    // Render above hazard floor tiles (water/lava/poison are at depth 2)
    this.setDepth(5);

    // Chicken drumstick: rotate so the bone points up
    if (foodType === FoodType.CHICKEN) {
      this.setAngle(180);
    }

    // Start with a pop-in effect
    this.setScale(0);
    scene.tweens.add({
      targets: this,
      scaleX: 1,
      scaleY: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });

    // Gentle floating animation
    this._createFloatAnimation();

    // Schedule disappearance
    this._lifetimeTimer = scene.time.delayedCall(lifetime, () => {
      this._fadeOut();
    });
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Called when the player picks up this food item.
   * Shows the food name with a zoom-fade effect and plays the
   * corresponding sound, then destroys itself.
   */
  collect() {
    // Immediately deactivate to prevent physics overlap firing again
    this.active = false;

    // Cancel the lifetime timer
    if (this._lifetimeTimer) {
      this._lifetimeTimer.destroy();
      this._lifetimeTimer = null;
    }

    // Guard: already destroyed (e.g. cleanup during level transition)
    if (!this.scene) return;

    // Play food collection sound
    const audioKey = FOOD_AUDIO_MAP[this.foodType];
    if (audioKey) {
      this.scene.sound.play(audioKey, { volume: getSfxVol(0.6) });
    }

    // Show the food name at the collection position with zoom-fade
    this._showCollectLabel();

    // Quick scale up + disappear
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 150,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.destroy();
      },
    });
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Gentle up-and-down floating while the item is alive.
   * @private
   */
  _createFloatAnimation() {
    this._floatTween = this.scene.tweens.add({
      targets: this,
      y: this.y - 3,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Show the food name at the collection spot with a zoom-in and fade-out
   * effect, giving the feeling of "picking something up".
   * @private
   */
  _showCollectLabel() {
    if (!this.scene) return;

    const name = FOOD_NAME_MAP[this.foodType] || '';
    if (!name) return;

    // Capture scene reference — `this.scene` will be null after the
    // FoodItem is destroyed, but the label lives independently.
    const scene = this.scene;

    const label = scene.add.text(
      this.x,
      this.y,
      name,
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2,
        fontStyle: 'bold',
      },
    );
    label.setOrigin(0.5, 0.5);
    label.setDepth(150);
    label.setScale(0.5);
    label.setAlpha(1);

    // Zoom in + hold + float up + fade out
    // First: quick zoom-in to full size
    scene.tweens.add({
      targets: label,
      scaleX: 2.2,
      scaleY: 2.2,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Hold visible briefly, then float up and fade
        if (!label.active) return;
        scene.tweens.add({
          targets: label,
          y: label.y - TILE_SIZE * 1.2,
          alpha: 0,
          duration: 1200,
          delay: 250,
          ease: 'Quad.easeIn',
          onComplete: () => {
            label.destroy();
          },
        });
      },
    });
  }

  /**
   * Fade out and blink before disappearing.
   * @private
   */
  _fadeOut() {
    // Guard: already destroyed (e.g. cleanup during level transition)
    if (!this.scene) return;

    // Blink effect: rapid alpha oscillation before final fade
    const blinkDuration = FOOD_FADE_DURATION * 0.6;
    const fadeDuration = FOOD_FADE_DURATION * 0.4;

    // Stop float
    if (this._floatTween) {
      this._floatTween.stop();
    }

    // Blink phase
    this.scene.tweens.add({
      targets: this,
      alpha: 0.3,
      duration: 100,
      yoyo: true,
      repeat: Math.floor(blinkDuration / 200),
      onComplete: () => {
        // Guard: scene may have been cleaned up during the blink
        if (!this.scene) return;

        // Final fade + shrink
        this.scene.tweens.add({
          targets: this,
          alpha: 0,
          scaleX: 0.3,
          scaleY: 0.3,
          duration: fadeDuration,
          ease: 'Quad.easeIn',
          onComplete: () => {
            this.destroy();
          },
        });
      },
    });
  }

  /**
   * Clean up timers and tweens before destruction.
   * @param {boolean} [fromScene] - Whether this Game Object is being destroyed by the Scene.
   */
  destroy(fromScene) {
    if (this._lifetimeTimer) {
      this._lifetimeTimer.destroy();
      this._lifetimeTimer = null;
    }
    if (this._floatTween) {
      this._floatTween.stop();
      this._floatTween = null;
    }
    super.destroy(fromScene);
  }
}
